'use client'

import React, { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import PreLoader from '@/components/PreLoader'
import Sidebar from '@/components/Sidebar'
import TopBar from '@/components/TopBar'
import KineticGrid from '@/components/ui/KineticGrid'
import { FiArrowRight, FiPlay } from 'react-icons/fi'
import { useStore } from '@/hooks/useStore'
import { useIsMobile } from '@/hooks/useIsMobile'
import { motion, Variants, AnimatePresence } from 'framer-motion'

const CanvasContainer = dynamic(() => import('@/three/CanvasContainer'), { ssr: false })
// Each of these sections' heavy deps (Matter.js physics, the Three.js globe, the particle
// canvas) only download once actually needed: on desktop that's driven by activeSection
// (dynamic-import's own on-render trigger); on mobile, since all 5 sections must exist in the
// DOM at once for free-scroll + the IntersectionObserver below to work, it's driven instead by
// the scroll-proximity loadedSections state further down — see the "lazy-load" effect in Home().
const AboutSection = dynamic(() => import('@/components/sections/AboutSection'), { ssr: false })
const ProjectsSection = dynamic(() => import('@/components/sections/ProjectsSection'), { ssr: false })
const ContactSection = dynamic(() => import('@/components/sections/ContactSection'), { ssr: false })
const Footer = dynamic(() => import('@/components/Footer'), { ssr: false })

export const ScrollContext = React.createContext<React.RefObject<HTMLElement | null> | null>(null)

const SECTION_ORDER = ['home', 'work', 'about', 'projects', 'contact']
// The 3 mobile sections whose components carry heavy dynamic-imported deps — these get
// scroll-proximity lazy-mounting on mobile instead of being present from the first paint.
const LAZY_SECTIONS = ['about', 'projects', 'contact']

const ScrollPositionController = ({ direction }: { direction: number }) => {
  const mainRef = React.useContext(ScrollContext)

  React.useLayoutEffect(() => {
    if (mainRef?.current) {
      if (direction === -1) {
        // Scrolled UP: Land perfectly at the bottom of the previous section
        mainRef.current.scrollTop = mainRef.current.scrollHeight
      } else {
        // Scrolled DOWN: Land at the top of the next section
        mainRef.current.scrollTop = 0
      }
    }
  }, [direction, mainRef])

  return null
}

export default function Home() {
  const { sidebarOpen, setSidebarOpen, isBooted, triggerWarp, activeSection, setActiveSection, scrollDirection, activeDepartment, theme } = useStore()
  const mainRef = useRef<HTMLElement>(null)
  const lastWarpTimeRef = useRef<number>(0)
  const isMobile = useIsMobile()
  // Mobile only: which of LAZY_SECTIONS have been triggered to actually mount their heavy
  // dynamic-imported component. Placeholders for all 3 exist in the DOM from the start (see
  // the JSX below) regardless of this state — only whether their real content is mounted
  // inside them depends on it. Never cleared once set, by design (see the effect below).
  const [loadedSections, setLoadedSections] = useState<Record<string, boolean>>({})

  // Removing the forceful scroll reset to top so ScrollPositionController handles it gracefully

  // Wheel/touch/keyboard "warp" between sections — desktop only. On mobile all sections are
  // stacked in normal document flow (see the isMobile branch in the JSX below) and scrolled
  // through natively; the IntersectionObserver effect further down keeps activeSection in
  // sync with scroll position instead of this gesture-driven jump.
  useEffect(() => {
    if (isMobile) return

    const order = SECTION_ORDER

    const triggerSection = (nextSec: string) => {
      lastWarpTimeRef.current = performance.now()
      if (mainRef.current) mainRef.current.scrollTop = 0
      window.scrollTo(0, 0)
      setActiveSection(nextSec)
    }

    const processDelta = (deltaY: number) => {
      const now = performance.now()
      if (now - lastWarpTimeRef.current < 1350) {
        return // Still inside the 1.35s cooldown window from our last warp! Do NOT allow any section jump!
      }

      if (Math.abs(deltaY) > 35) {
        const mainEl = mainRef.current
        const mainScrollTop = mainEl ? mainEl.scrollTop : 0
        const mainScrollHeight = mainEl ? mainEl.scrollHeight : 0
        const mainClientHeight = mainEl ? mainEl.clientHeight : 0
        const winScrollTop = window.scrollY || document.documentElement.scrollTop
        const winScrollHeight = document.documentElement.scrollHeight
        const winClientHeight = window.innerHeight

        const atTop = winScrollTop <= 15 && mainScrollTop <= 15

        // Only check container if it actually has its own scrollbar taller than view height
        const isContainerScrollable = mainClientHeight > 0 && mainScrollHeight > mainClientHeight + 20
        const isContainerAtBottom = isContainerScrollable && (mainScrollTop + mainClientHeight >= mainScrollHeight - 10)

        // Only check window if window actually has a scrollbar taller than window height
        const isWindowScrollable = winScrollHeight > winClientHeight + 20
        const isWindowAtBottom = isWindowScrollable && (winClientHeight + winScrollTop >= winScrollHeight - 10)

        // If the current section has no scrollbar (e.g. fixed 3D sections), we consider it both at top and at bottom natively
        const atBottom = isContainerAtBottom || isWindowAtBottom || (!isContainerScrollable && !isWindowScrollable)

        if (activeSection === 'home') {
          if (deltaY > 0) {
            triggerSection('work')
          }
        } else if (activeSection === 'work') {
          if (deltaY < 0 && atTop) {
            triggerSection('home')
          } else if (deltaY > 0 && atBottom) {
            triggerSection('about')
          }
        } else if (activeSection !== 'home') {
          const idx = order.indexOf(activeSection)
          if (deltaY < 0 && atTop && idx > 0) {
            triggerSection(order[idx - 1])
          } else if (deltaY > 0 && atBottom && idx < order.length - 1 && idx !== -1) {
            triggerSection(order[idx + 1])
          }
        }
      }
    }

    const handleWheel = (e: WheelEvent) => processDelta(e.deltaY)

    let touchStartY = 0
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY
    }
    const handleTouchEnd = (e: TouchEvent) => {
      processDelta(touchStartY - e.changedTouches[0].clientY)
    }

    // Keyboard section navigation: Arrow/Page keys jump between screens, mirroring
    // the wheel gesture, so the site is usable without a mouse. Skipped while
    // typing in a form field so it doesn't hijack normal text editing.
    const handleKeyDown = (e: KeyboardEvent) => {
      const isNextKey = e.key === 'ArrowDown' || e.key === 'PageDown'
      const isPrevKey = e.key === 'ArrowUp' || e.key === 'PageUp'
      if (!isNextKey && !isPrevKey) return

      const target = e.target as HTMLElement
      if (target.closest('input, textarea, select, [contenteditable="true"]')) return

      if (performance.now() - lastWarpTimeRef.current < 1350) return

      const idx = order.indexOf(activeSection)
      const nextIdx = isNextKey ? idx + 1 : idx - 1
      if (idx === -1 || nextIdx < 0 || nextIdx >= order.length) return

      e.preventDefault()
      triggerSection(order[nextIdx])
    }

    window.addEventListener('wheel', handleWheel, { passive: true })
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchend', handleTouchEnd, { passive: true })
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeSection, setActiveSection, triggerWarp, isMobile])

  // Mobile: keep activeSection in sync with whichever stacked section is centered in the
  // viewport as the user free-scrolls, since it's no longer driven by triggerSection above.
  // Watches a thin virtual line at the viewport's vertical center (via rootMargin) rather than
  // comparing intersectionRatios — simpler, and gives the same "whichever section is centered
  // wins" result. Goes straight to the store's setState instead of the setActiveSection action
  // so passive scroll updates don't also trigger its warpSpeed flash or push a history entry on
  // every section crossed — those side effects are for deliberate nav actions (clicks/warp),
  // not every tick of a continuous scroll. activeDepartment is still cleared to match what
  // setActiveSection normally does, so a department's expanded card panel can't get stuck open
  // while the user scrolls away from Work.
  useEffect(() => {
    if (!isMobile) return

    const elements = SECTION_ORDER
      .map((id) => document.getElementById(`section-${id}`))
      .filter((el): el is HTMLElement => !!el)
    if (!elements.length) return

    const idByElement = new Map(elements.map((el) => [el, el.id.replace('section-', '')]))

    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.filter((e) => e.isIntersecting)
        if (!intersecting.length) return
        // A fast flick can cross the center line for two adjacent sections in the same tick;
        // prefer whichever is further along in section order to match forward scroll intent.
        const ids = intersecting
          .map((e) => idByElement.get(e.target as HTMLElement))
          .filter((id): id is string => !!id)
        const nextId = ids.sort((a, b) => SECTION_ORDER.indexOf(b) - SECTION_ORDER.indexOf(a))[0]
        if (nextId) useStore.setState({ activeSection: nextId, activeDepartment: null })
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [isMobile])

  // Mobile: mount each heavy section's real dynamic-imported component once the user is
  // roughly a viewport away from reaching it, instead of leaving all 3 mounted from the first
  // paint (which would force Matter.js/Three.js/the particle canvas to download upfront). A
  // separate observer from the one above — that one watches a thin center line for the active
  // section; this one uses a generous rootMargin so loading starts well before arrival, giving
  // the network/JS-parse time to finish before the section is actually visible. Once a section
  // is marked loaded it's unobserved and never un-loaded — re-mounting a Matter.js engine or
  // Three.js scene on every scroll-away would be worse than just keeping it mounted.
  useEffect(() => {
    if (!isMobile) return

    const elements = LAZY_SECTIONS
      .map((id) => document.getElementById(`section-${id}`))
      .filter((el): el is HTMLElement => !!el)
    if (!elements.length) return

    const idByElement = new Map(elements.map((el) => [el, el.id.replace('section-', '')]))

    const observer = new IntersectionObserver(
      (entries) => {
        const newlyNear = entries.filter((e) => e.isIntersecting)
        if (!newlyNear.length) return

        setLoadedSections((prev) => {
          const next = { ...prev }
          let changed = false
          for (const entry of newlyNear) {
            const id = idByElement.get(entry.target as HTMLElement)
            if (id && !next[id]) {
              next[id] = true
              changed = true
            }
          }
          return changed ? next : prev
        })

        newlyNear.forEach((entry) => observer.unobserve(entry.target))
      },
      { rootMargin: '100% 0px' }
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [isMobile])

  // Mobile: tap bridge for the Work section's department cards and their expanded panel's
  // close button. <main> is pointer-events-auto on mobile (required for iOS touch-scroll —
  // see the wrapper/<main> comment above), and #section-work is an empty scroll-flow spacer
  // inside it (see the JSX below) — the actual department cards (and the panel that opens
  // when one's tapped) live behind both, in the fixed 3D background layer. That means every
  // tap over the Work zone lands on the empty spacer instead of the real content.
  //
  // Originally tried temporarily setting <main> to pointer-events:none and using
  // document.elementFromPoint to find whatever's underneath, then dispatching a synthetic
  // click there. Real-device testing (both iOS and Android) showed elementFromPoint kept
  // returning <main> itself even after the style change and a forced layout flush — a
  // synchronous-repaint limitation of that API on mobile browsers, not a logic bug. Abandoned
  // in favor of manual hit-testing: department cards carry a data-department-id attribute and
  // the panel's close button carries data-panel-close (see CanvasContainer.tsx), so a genuine
  // tap over #section-work is checked against the relevant element(s)' getBoundingClientRect()
  // directly, and the store action is called straight — no DOM event dispatch, no dependency
  // on hit-testing timing. Covers all three ways to interact with this flow: opening a card,
  // its panel's close button, and tapping the dimmed backdrop outside the panel to dismiss it.
  useEffect(() => {
    if (!isMobile) return
    const mainEl = mainRef.current
    if (!mainEl) return

    const isPointInRect = (x: number, y: number, rect: DOMRect) =>
      x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom

    const handleClick = (e: MouseEvent) => {
      if (useStore.getState().activeSection !== 'work') return

      const target = e.target as HTMLElement
      if (!target.closest('#section-work')) return

      const activeDepartment = useStore.getState().activeDepartment
      if (activeDepartment) {
        // Panel is open. Three zones, checked in order: the close button (closes), the
        // currently-open card's own rect (does nothing — a tap there shouldn't accidentally
        // close the panel, even though the card is visually behind the backdrop blur), and
        // everywhere else (treated as the dimmed backdrop — closes, matching desktop's
        // click-outside-to-dismiss via stopPropagation on the panel's dialog card).
        const closeBtn = document.querySelector<HTMLElement>('[data-panel-close]')
        if (closeBtn && isPointInRect(e.clientX, e.clientY, closeBtn.getBoundingClientRect())) {
          useStore.getState().setActiveDepartment(null)
          return
        }

        const activeCard = document.querySelector<HTMLElement>(`[data-department-id="${activeDepartment}"]`)
        const insideActiveCard = !!activeCard && isPointInRect(e.clientX, e.clientY, activeCard.getBoundingClientRect())
        if (insideActiveCard) return

        useStore.getState().setActiveDepartment(null)
        return
      }

      const cards = document.querySelectorAll<HTMLElement>('[data-department-id]')
      for (const card of cards) {
        if (isPointInRect(e.clientX, e.clientY, card.getBoundingClientRect())) {
          const id = card.dataset.departmentId
          if (id) useStore.getState().setActiveDepartment(id)
          break
        }
      }
    }

    mainEl.addEventListener('click', handleClick)
    return () => mainEl.removeEventListener('click', handleClick)
  }, [isMobile])

  // URL hash sync: land on the right section when a #section link is opened,
  // and support browser back/forward through section history.
  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace('#', '')
      const current = useStore.getState().activeSection
      if (hash && SECTION_ORDER.includes(hash) && hash !== current) {
        if (isMobile) {
          document.getElementById(`section-${hash}`)?.scrollIntoView({ block: 'start' })
        } else {
          useStore.getState().setActiveSection(hash)
        }
      }
    }
    applyHash()
    window.addEventListener('popstate', applyHash)
    return () => window.removeEventListener('popstate', applyHash)
  }, [isMobile])

  const directionalVariants: Variants = {
    hidden: (direction: number = 1) => ({
      opacity: 0,
      y: direction > 0 ? 64 : -64,
    }),
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
    },
    exit: (direction: number = 1) => ({
      opacity: 0,
      y: direction > 0 ? -64 : 64,
      transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
    }),
  }

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.18,
        delayChildren: 0.15,
      },
    },
  }

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
    },
  }

  const headlineContainerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2,
      },
    },
  }

  const wordVariant: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
    },
  }

  return (
    <div className="flex min-h-dvh select-none bg-bg-base overflow-hidden relative transition-colors duration-500">
      <PreLoader />
      {/* 3D Scene in Background — Stationary full-screen with smooth cinematic blur when navigation overlay opens */}
      <div
        className={`fixed inset-0 w-full h-full z-1 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          sidebarOpen ? 'blur-[14px] brightness-[0.45] pointer-events-none scale-102' : ''
        } ${activeSection === 'home' ? 'scale-[0.58] -translate-y-[27%] md:scale-100 md:translate-y-0' : ''}`}
      >
        {/* Kinetic Grid (Deep Background) */}
        <AnimatePresence>
          {activeSection === 'work' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.85 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              className="absolute inset-0 z-0 pointer-events-auto overflow-hidden"
            >
              <KineticGrid
                background="transparent"
                dotColor={theme === 'light' ? '#000000' : '#a3e635'}
                lineColor={theme === 'light' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(163, 230, 53, 0.45)'}
                trailColor={theme === 'light' ? '#000000' : '#a3e635'}
                spacing={48}
                radius={320}
                strength={6}
                trail={true}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Why Us? Text Overlay — pt-20/text-2xl/pb-3 on mobile (vs. pt-36/text-4xl+/pb-6 on
            desktop, both untouched) claws back roughly 70-80px of vertical space at the top of
            short Android viewports, so this header doesn't collide with the department card
            constellation below it. See CanvasContainer.tsx's OrbitingSystem for the matching
            card-side vertical compression — the two changes work together. */}
        <AnimatePresence mode="wait" custom={scrollDirection}>
          {activeSection === 'work' && (
            <motion.div
              custom={scrollDirection}
              variants={directionalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute top-0 left-0 right-0 z-20 w-full px-6 md:px-12 lg:px-24 pt-20 md:pt-36 pointer-events-none"
            >
              <div className="w-full max-w-[1400px] mx-auto flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-glass/10 pb-3 md:pb-6">
                <h2 className="font-display font-extrabold text-2xl md:text-4xl lg:text-5xl text-text-title tracking-tight leading-[1.1]">
                  Everything You Need <br />
                  <span className="text-brand-green glow-text-green font-creative font-normal tracking-wide text-[1.05em] inline-block mt-1">Under One Roof.</span>
                </h2>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Static hero globe — server-rendered stand-in for the LCP metric. The real globe lives
            inside CanvasContainer's WebGL scene (dynamic, ssr:false, mounted via a drei <Html>
            portal), so it structurally cannot exist in the initial HTML: nothing paints there
            until Three.js/R3F download, parse, execute, and mount. This next/image copy paints
            immediately from the server-rendered document instead. Unmounted once booted — it's
            hidden behind the fully opaque PreLoader for its entire lifetime anyway (nobody ever
            sees it), so an animated handoff would just be extra paint work for no visible benefit. */}
        {activeSection === 'home' && !isBooted && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
            <div className="relative w-[360px] h-[360px] md:w-[420px] md:h-[420px]">
              <div className="absolute inset-0 rounded-full bg-radial from-transparent via-transparent to-[#060509] z-10 scale-105" />
              <Image
                src="/globe_3d.webp"
                alt="COR-PIX Crystal Globe"
                fill
                priority
                sizes="(min-width: 768px) 420px, 360px"
                className="object-contain rounded-full drop-shadow-[0_0_50px_rgba(163,230,53,0.18)]"
              />
            </div>
          </div>
        )}

        {/* 3D Scene (Foreground) */}
        <div className={`relative w-full h-full transition-all duration-300 ${activeDepartment ? 'z-30' : 'z-10'}`}>
          <CanvasContainer />
        </div>
      </div>

      {/* Click-Outside Backdrop Layer to cleanly close overlay menu */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-bg-base/70 backdrop-blur-[4px] cursor-pointer transition-opacity duration-300"
        />
      )}

      {/* Persistent Left Sidebar — Navigation Drawer Overlay (z-50) */}
      <Sidebar />

      {/* Main Content Area — Stationary full-screen with cinematic blur when navigation opens.
          pointer-events is auto on mobile (needed for iOS to attach touch-scroll handling to
          <main> below — see the isMobile <main> below) and none on desktop (unchanged,
          decorative-passthrough design), except while the sidebar overlay is open, where it's
          always none so the blurred-out background can't be scrolled/tapped behind the drawer. */}
      <div
        className={`flex-1 flex flex-col justify-between h-dvh max-h-dvh overflow-hidden z-10 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          sidebarOpen ? 'blur-[14px] brightness-[0.45] pointer-events-none scale-[0.98]' : (isMobile ? 'pointer-events-auto' : 'pointer-events-none')
        }`}
      >
        {/* Sticky Actions Top Bar */}
        <TopBar />

        {isMobile ? (
          /* Mobile: normal scrollable document flow through all 5 stacked sections, synced to
             activeSection via the IntersectionObserver above instead of the desktop warp system.
             pointer-events-auto here (not -none like desktop's <main> below) — iOS Safari
             disqualifies an overflow-y-auto element from touch-scroll handling entirely when
             the element itself has pointer-events:none, regardless of its children. */
          <main ref={mainRef} className="flex-1 flex flex-col px-6 relative pointer-events-auto overflow-y-auto h-full">
            <ScrollContext.Provider value={mainRef}>
              <div id="section-home" className="w-full min-h-dvh flex flex-col shrink-0">
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate={isBooted ? 'visible' : 'hidden'}
                  className="max-w-[500px] flex flex-col gap-4 relative z-10 pointer-events-none mt-[32vh]"
                >
                  {/* Headline with Word-by-Word Staggered Entrance */}
                  <motion.h1
                    variants={headlineContainerVariants}
                    initial="hidden"
                    animate={isBooted ? 'visible' : 'hidden'}
                    className="font-display font-extrabold text-text-title text-4xl leading-[1.1] tracking-tight flex flex-wrap items-baseline gap-x-3 gap-y-1 drop-shadow-md"
                  >
                    <motion.span variants={wordVariant} className="inline-block">We</motion.span>
                    <motion.span variants={wordVariant} className="inline-block">design</motion.span>
                    <div className="w-full h-0" />
                    <motion.span variants={wordVariant} className="inline-block">digital</motion.span>
                    <motion.span variants={wordVariant} className="inline-block">experiences</motion.span>
                    <div className="w-full h-0" />
                    <motion.span variants={wordVariant} className="inline-block">that</motion.span>
                    <motion.span variants={wordVariant} className="inline-block">drive</motion.span>
                    <motion.span
                      variants={wordVariant}
                      className="font-creative font-normal text-brand-green glow-text-green tracking-wide text-[1.15em] inline-block ml-1"
                    >
                      impact.
                    </motion.span>
                  </motion.h1>

                  {/* Subtitle */}
                  <motion.p
                    variants={itemVariants}
                    className="text-sm font-sans text-text-body font-medium leading-relaxed max-w-[420px]"
                  >
                    An integrated creative ecosystem where ideas, design and
                    technology come together to build powerful brand experiences.
                  </motion.p>

                  {/* CTA Buttons */}
                  <motion.div variants={itemVariants} className="flex items-center gap-5 pt-2 pointer-events-auto">
                    <button
                      onClick={() => document.getElementById('section-work')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="btn-clay-pill btn-clay-primary flex items-center gap-2 text-xs font-bold uppercase tracking-wider cursor-pointer"
                    >
                      Explore Workspace
                      <FiArrowRight className="w-4.5 h-4.5" />
                    </button>

                    <button className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-text-subtle hover:text-text-title transition-colors duration-300 cursor-pointer">
                      <div className="w-8 h-8 rounded-full border border-glass/10 flex items-center justify-center bg-glass/5">
                        <FiPlay className="w-3.5 h-3.5 fill-glass/10 text-text-subtle" />
                      </div>
                      How it works
                    </button>
                  </motion.div>
                </motion.div>
              </div>

              {/* No in-flow visual content of its own — "Why Us?" is entirely the fixed 3D
                  background overlay above (KineticGrid + text + department cards), gated on
                  activeSection === 'work'. This is a pure scroll-flow spacer so the
                  IntersectionObserver above has something to observe for that dwell zone. */}
              <div id="section-work" className="w-full h-dvh shrink-0" />

              {/* Placeholder is always present (id + min-h-dvh, unconditional) so scroll
                  position and both observers' geometry never shift; only the actual heavy
                  component's mount is gated on loadedSections. */}
              <div id="section-about" className="w-full min-h-dvh pointer-events-auto shrink-0">
                {loadedSections.about && <AboutSection />}
              </div>

              <div id="section-projects" className="w-full min-h-dvh pointer-events-auto shrink-0">
                {loadedSections.projects && <ProjectsSection />}
              </div>

              <div id="section-contact" className="w-full min-h-dvh flex flex-col items-center pointer-events-auto shrink-0">
                {loadedSections.contact && (
                  <>
                    <ContactSection />
                    <Footer />
                  </>
                )}
              </div>
            </ScrollContext.Provider>
          </main>
        ) : (
          /* Desktop: single active section at a time via AnimatePresence — unchanged. */
          <main ref={mainRef} className={`flex-1 flex px-6 md:px-16 relative pointer-events-none overflow-y-auto h-full ${activeSection === 'home' ? 'items-start md:items-center' : 'items-start'}`}>
            <ScrollContext.Provider value={mainRef}>
              <AnimatePresence mode="wait" custom={scrollDirection}>
                {activeSection === 'home' && (
                <motion.div
                  key="home"
                  custom={scrollDirection}
                  variants={containerVariants}
                  initial="hidden"
                  animate={isBooted ? 'visible' : 'hidden'}
                  exit={scrollDirection > 0 ? { opacity: 0, y: -64, transition: { duration: 0.35 } } : { opacity: 0, y: 64, transition: { duration: 0.35 } }}
                  className="max-w-[500px] flex flex-col gap-4 md:gap-6 relative z-10 pointer-events-none mt-[32vh] md:mt-0"
                >
                  <ScrollPositionController direction={scrollDirection} />
                  {/* Headline with Word-by-Word Staggered Entrance */}
                  <motion.h1
                    variants={headlineContainerVariants}
                    initial="hidden"
                    animate={isBooted ? 'visible' : 'hidden'}
                    className="font-display font-extrabold text-text-title text-4xl md:text-5xl leading-[1.1] tracking-tight flex flex-wrap items-baseline gap-x-3 gap-y-1 drop-shadow-md"
                  >
                    <motion.span variants={wordVariant} className="inline-block">We</motion.span>
                    <motion.span variants={wordVariant} className="inline-block">design</motion.span>
                    <div className="w-full h-0" />
                    <motion.span variants={wordVariant} className="inline-block">digital</motion.span>
                    <motion.span variants={wordVariant} className="inline-block">experiences</motion.span>
                    <div className="w-full h-0" />
                    <motion.span variants={wordVariant} className="inline-block">that</motion.span>
                    <motion.span variants={wordVariant} className="inline-block">drive</motion.span>
                    <motion.span
                      variants={wordVariant}
                      className="font-creative font-normal text-brand-green glow-text-green tracking-wide text-[1.15em] inline-block ml-1"
                    >
                      impact.
                    </motion.span>
                  </motion.h1>

                  {/* Subtitle */}
                  <motion.p
                    variants={itemVariants}
                    className="text-sm font-sans text-text-body font-medium leading-relaxed max-w-[420px]"
                  >
                    An integrated creative ecosystem where ideas, design and
                    technology come together to build powerful brand experiences.
                  </motion.p>

                  {/* CTA Buttons */}
                  <motion.div variants={itemVariants} className="flex items-center gap-5 pt-2 pointer-events-auto">
                    <button
                      onClick={() => setActiveSection('work')}
                      className="btn-clay-pill btn-clay-primary flex items-center gap-2 text-xs font-bold uppercase tracking-wider cursor-pointer"
                    >
                      Explore Workspace
                      <FiArrowRight className="w-4.5 h-4.5" />
                    </button>

                    <button className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-text-subtle hover:text-text-title transition-colors duration-300 cursor-pointer">
                      <div className="w-8 h-8 rounded-full border border-glass/10 flex items-center justify-center bg-glass/5">
                        <FiPlay className="w-3.5 h-3.5 fill-glass/10 text-text-subtle" />
                      </div>
                      How it works
                    </button>
                  </motion.div>
                </motion.div>
              )}

              {activeSection === 'about' && (
                <motion.div
                  key="about"
                  custom={scrollDirection}
                  variants={directionalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="w-full pointer-events-auto"
                >
                  <ScrollPositionController direction={scrollDirection} />
                  <AboutSection />
                </motion.div>
              )}

              {activeSection === 'projects' && (
                <motion.div
                  key="projects"
                  custom={scrollDirection}
                  variants={directionalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="w-full pointer-events-auto"
                >
                  <ScrollPositionController direction={scrollDirection} />
                  <ProjectsSection />
                </motion.div>
              )}

              {activeSection === 'contact' && (
                <motion.div
                  key="contact"
                  custom={scrollDirection}
                  variants={directionalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="w-full flex flex-col items-center pointer-events-auto"
                >
                  <ScrollPositionController direction={scrollDirection} />
                  <ContactSection />
                  <Footer />
                </motion.div>
              )}

              {activeSection !== 'home' && activeSection !== 'work' && activeSection !== 'about' && activeSection !== 'projects' && activeSection !== 'contact' && (
                <motion.div
                  key="other-section"
                  custom={scrollDirection}
                  variants={directionalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="max-w-[560px] mx-auto p-8 rounded-2xl bg-glass/[0.04] backdrop-blur-[24px] border border-glass/15 text-center flex flex-col gap-4 pointer-events-auto my-auto shadow-2xl"
                >
                  <ScrollPositionController direction={scrollDirection} />
                  <div className="w-12 h-12 rounded-xl bg-brand-green/10 border border-brand-green/30 text-brand-green flex items-center justify-center mx-auto text-xl font-mono">
                    //
                  </div>
                  <h3 className="font-display font-bold text-text-title text-2xl uppercase tracking-wide">
                    {activeSection} Section
                  </h3>
                  <p className="text-xs font-sans text-text-body leading-relaxed">
                    Module architecture initialized. This dedicated interactive screen is queued for deployment in our upcoming step.
                  </p>
                  <button
                    onClick={() => setActiveSection('home')}
                    className="mt-2 px-6 py-2.5 rounded-full bg-glass/10 border border-glass/20 text-text-title font-mono text-xs hover:border-brand-green hover:text-brand-green transition-colors mx-auto cursor-pointer"
                  >
                    ← Return to Home Matrix
                  </button>
                </motion.div>
              )}
              </AnimatePresence>
            </ScrollContext.Provider>
          </main>
        )}

        {/* Animated Mouse Scrolling Indicator - Bottom Right Corner */}
        <div className="fixed bottom-8 right-10 z-20 pointer-events-none select-none transition-all duration-500">
          <div className="w-6 h-10 border-2 border-glass/85 rounded-full flex justify-center pt-2 shadow-[0_0_15px_rgba(255,255,255,0.05)] backdrop-blur-[2px]">
            <motion.div
              animate={{
                y: [0, 10, 0],
                opacity: [1, 0.35, 1],
              }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="w-1.5 h-3 bg-brand-green rounded-full shadow-[0_0_10px_#a3e635]"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
